import { useState, useRef, useEffect, useMemo } from 'react';
import { Globe, Folder, Tag, X, ChevronDown, Search } from 'lucide-react';
import IconRenderer from '../ui/IconRenderer';
import useContextStore from '../../stores/useContextStore';
import useProjectStore from '../../stores/useProjectStore';
import usePropertyStore from '../../stores/usePropertyStore';
import { resolveEntityColor } from '../../utils/entityColor';
import './ContextSwitcher.css';

/**
 * ContextSwitcher — global context filter.
 *
 * Supports two context types:
 * 1. Project  — filter everything to a specific project
 * 2. Property — filter by a custom property value (e.g. Client=MYSELF)
 *
 * Both write `active_context`, which Today / AllTasks / Projects / Dashboard read
 * through `useContextFilter`. It is gated by the `contextFilter` module alongside
 * the Work/Life toggle — the two are the same idea to a user (filter the whole
 * app by a dimension), so they share one switch and one home.
 *
 * @param {boolean} collapsed  icon-only trigger (the sidebar rail)
 * @param {'sidebar'|'inline'} variant
 *        'sidebar' — full-width block with its own vertical rhythm (original home)
 *        'inline'  — hugs its content for a horizontal utility bar (the header)
 *        Only spacing and dropdown anchoring differ; the control is identical.
 */
export default function ContextSwitcher({ collapsed, variant = 'sidebar' }) {
    const activeContext = useContextStore(s => s.activeContext);
    const setContext = useContextStore(s => s.setContext);
    const clearContext = useContextStore(s => s.clearContext);

    const projects = useProjectStore(s => s.projects);
    const properties = usePropertyStore(s => s.properties);

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    // Focus search on open
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Build options
    const projectOptions = useMemo(() => {
        const q = search.toLowerCase();
        return projects
            .filter(p => p.status !== 'archived')
            .filter(p => !q || p.name.toLowerCase().includes(q))
            .map(p => ({
                type: 'project',
                id: p.id,
                label: p.name,
                entityIcon: p.icon || p.emoji || '📁',
                status: p.status,
            }));
    }, [projects, search]);

    // Build property value options from Select/Multi-select properties
    const propertyOptions = useMemo(() => {
        const q = search.toLowerCase();
        const opts = [];

        // Only include Select and Multi-Select properties
        properties
            .filter(p => p.type === 'select' || p.type === 'multi-select')
            .forEach(prop => {
                (prop.options || []).forEach(opt => {
                    const label = `${prop.name}: ${opt.label}`;
                    if (!q || label.toLowerCase().includes(q)) {
                        opts.push({
                            type: 'property',
                            key: prop.id,
                            keyLabel: prop.name,
                            value: opt.id,
                            valueLabel: opt.label,
                            label: label,
                            color: opt.color,
                        });
                    }
                });
            });

        return opts;
    }, [properties, search]);

    // Get label for active context
    const contextLabel = useMemo(() => {
        if (!activeContext) return null;
        if (activeContext.type === 'project') {
            const p = projects.find(p => p.id === activeContext.id);
            return p ? { entityIcon: p.icon || p.emoji || '📁', text: p.name } : { entityIcon: '📁', text: 'Unknown Project' };
        }
        if (activeContext.type === 'property') {
            const prop = properties.find(p => p.id === activeContext.key);
            const propName = prop?.name || activeContext.key;
            const opt = prop?.options?.find(o => o.id === activeContext.value);
            const valLabel = opt?.label || activeContext.value;
            return { entityIcon: '🏷️', text: `${propName}: ${valLabel}` };
        }
        return null;
    }, [activeContext, projects, properties]);

    const handleSelect = (option) => {
        if (option.type === 'project') {
            setContext({ type: 'project', id: option.id });
        } else if (option.type === 'property') {
            setContext({ type: 'property', key: option.key, value: option.value });
        }
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        clearContext();
    };

    const rootClass = (...extra) =>
        ['context-switcher', `context-switcher--${variant}`, ...extra].filter(Boolean).join(' ');

    // ── Collapsed mode ──
    if (collapsed) {
        return (
            <div className={rootClass('context-switcher--collapsed')} ref={dropdownRef}>
                <button
                    className={`context-switcher__trigger context-switcher__trigger--collapsed ${activeContext ? 'context-switcher__trigger--active' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    title={contextLabel ? `Context: ${contextLabel.text}` : 'Set Context Filter'}
                >
                    {activeContext ? (
                        <span className="context-switcher__emoji"><IconRenderer icon={contextLabel?.entityIcon} size={14} /></span>
                    ) : (
                        <Globe size={16} />
                    )}
                </button>

                {isOpen && (
                    <div className="context-switcher__dropdown context-switcher__dropdown--collapsed">
                        <DropdownContent
                            search={search}
                            setSearch={setSearch}
                            inputRef={inputRef}
                            projectOptions={projectOptions}
                            propertyOptions={propertyOptions}
                            activeContext={activeContext}
                            onSelect={handleSelect}
                            onClear={() => { clearContext(); setIsOpen(false); }}
                        />
                    </div>
                )}
            </div>
        );
    }

    // ── Expanded mode ──
    return (
        <div className={rootClass()} ref={dropdownRef}>
            <button
                className={`context-switcher__trigger ${activeContext ? 'context-switcher__trigger--active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {activeContext ? (
                    <>
                        <span className="context-switcher__emoji"><IconRenderer icon={contextLabel?.entityIcon} size={14} /></span>
                        <span className="context-switcher__label">{contextLabel?.text}</span>
                        <button className="context-switcher__clear" onClick={handleClear} title="Clear filter">
                            <X size={12} />
                        </button>
                    </>
                ) : (
                    <>
                        <Globe size={14} />
                        <span className="context-switcher__label">All Contexts</span>
                        <ChevronDown size={12} className={`context-switcher__chevron ${isOpen ? 'context-switcher__chevron--open' : ''}`} />
                    </>
                )}
            </button>

            {isOpen && (
                <div className="context-switcher__dropdown">
                    <DropdownContent
                        search={search}
                        setSearch={setSearch}
                        inputRef={inputRef}
                        projectOptions={projectOptions}
                        propertyOptions={propertyOptions}
                        activeContext={activeContext}
                        onSelect={handleSelect}
                        onClear={() => { clearContext(); setIsOpen(false); }}
                    />
                </div>
            )}
        </div>
    );
}

function DropdownContent({ search, setSearch, inputRef, projectOptions, propertyOptions, activeContext, onSelect, onClear }) {
    return (
        <>
            <div className="context-switcher__search">
                <Search size={14} />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search contexts..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="context-switcher__search-input"
                />
            </div>

            <div className="context-switcher__options">
                {/* Clear option */}
                {activeContext && (
                    <button className="context-switcher__option context-switcher__option--clear" onClick={onClear}>
                        <Globe size={14} />
                        <span>All Contexts</span>
                        <span className="context-switcher__option-hint">Clear filter</span>
                    </button>
                )}

                {/* Projects */}
                {projectOptions.length > 0 && (
                    <>
                        <div className="context-switcher__section">Projects</div>
                        {projectOptions.map(opt => (
                            <button
                                key={opt.id}
                                className={`context-switcher__option ${activeContext?.type === 'project' && activeContext?.id === opt.id ? 'context-switcher__option--selected' : ''}`}
                                onClick={() => onSelect(opt)}
                            >
                                <span className="context-switcher__emoji"><IconRenderer icon={opt.entityIcon} size={14} /></span>
                                <span>{opt.label}</span>
                                <span className="context-switcher__option-status">{opt.status}</span>
                            </button>
                        ))}
                    </>
                )}

                {/* Properties */}
                {propertyOptions.length > 0 && (
                    <>
                        <div className="context-switcher__section">Properties</div>
                        {propertyOptions.map((opt, i) => (
                            <button
                                key={`${opt.key}-${opt.value}-${i}`}
                                className={`context-switcher__option ${activeContext?.type === 'property' && activeContext?.key === opt.key && activeContext?.value === opt.value ? 'context-switcher__option--selected' : ''}`}
                                onClick={() => onSelect(opt)}
                            >
                                <Tag size={14} style={{ color: resolveEntityColor(opt.color, 'var(--text-muted)') }} />
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </>
                )}

                {projectOptions.length === 0 && propertyOptions.length === 0 && (
                    <div className="context-switcher__empty">No matching contexts</div>
                )}
            </div>
        </>
    );
}
